import { GOOGLE_PLACE_TYPE_TO_BUSINESS_TYPE } from "@/web/lib/googlePlaces/googlePlaceTypeToBusinessType";
import type { BusinessCategoryType } from "@/web/lib/schemaUtils/businessCategories";

/** Google `Place` after `fetchFields` — only fields we read are required. */
export type PlaceLike = {
  addressComponents?: google.maps.places.AddressComponent[] | null;
  displayName?: string | LocalizedText | null;
  primaryType?: string | null;
  primaryTypeDisplayName?: string | LocalizedText | null;
  types?: string[] | null;
};

export type LocalizedText = { text: string; languageCode?: string };

export function placeDisplayName(place: PlaceLike): string {
  const dn = place.displayName;
  if (dn == null) return "Unknown";
  if (typeof dn === "string") return dn;
  if (typeof dn === "object" && "text" in dn && typeof (dn as LocalizedText).text === "string") {
    return (dn as LocalizedText).text;
  }
  return "Unknown";
}

export function placeWebsiteUrl(place: {
  websiteURI?: string | null;
  websiteUri?: string | null;
}): string {
  const u = place.websiteURI ?? place.websiteUri;
  return typeof u === "string" ? u : "";
}

export function placePrimaryTypeDisplayName(place: PlaceLike): string | null {
  const v = place.primaryTypeDisplayName;
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object" && "text" in v && typeof (v as LocalizedText).text === "string") {
    return (v as LocalizedText).text;
  }
  return null;
}

/**
 * Address + business-type helpers aligned with ploutos `PlaceFormatters`
 * (`shared/googlePlaces.ts`).
 */
export const PlaceFormatters = {
  getStreetAddress(place: PlaceLike): string | null {
    const components = place.addressComponents ?? [];
    let streetNumber = "";
    let route = "";
    for (const c of components) {
      if (c.types.includes("street_number")) streetNumber = c.shortText ?? "";
      if (c.types.includes("route")) route = c.shortText ?? "";
    }
    return streetNumber && route ? `${streetNumber} ${route}` : null;
  },

  getCity(place: PlaceLike): string | null {
    const components = place.addressComponents ?? [];
    return (
      components.find((c) => c.types.includes("locality"))?.shortText ??
      components.find((c) => c.types.includes("sublocality_level_1"))?.shortText ??
      components.find((c) => c.types.includes("administrative_area_level_3"))?.shortText ??
      null
    );
  },

  getState(place: PlaceLike): string | null {
    const components = place.addressComponents ?? [];
    return (
      components.find((c) => c.types.includes("administrative_area_level_1"))?.shortText ?? null
    );
  },

  getCityState(place: PlaceLike): string | null {
    const city = this.getCity(place);
    const state = this.getState(place);
    return city && state ? `${city}, ${state}` : null;
  },

  getBusinessCategoryType(place: PlaceLike): BusinessCategoryType {
    const googleType =
      place.primaryType ?? place.types?.find((t) => GOOGLE_PLACE_TYPE_TO_BUSINESS_TYPE[t]);
    if (!googleType) return "other.other";
    return GOOGLE_PLACE_TYPE_TO_BUSINESS_TYPE[googleType] ?? "other.other";
  },

  getAddressParts(place: PlaceLike): {
    addressLine1: string;
    addressLine2: string | undefined;
    city: string;
    state: string;
    zip: string;
    countryCode: string;
  } {
    const components = place.addressComponents ?? [];
    return {
      addressLine1: this.getStreetAddress(place) ?? "",
      addressLine2: undefined,
      city: this.getCity(place) ?? "",
      state: this.getState(place) ?? "",
      zip: components.find((c) => c.types.includes("postal_code"))?.shortText ?? "",
      countryCode: components.find((c) => c.types.includes("country"))?.shortText ?? "US",
    };
  },
};
